import React, { useState, useMemo } from 'react';
import {
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Search,
  FilterList,
  Download,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import { formatCurrency } from './KPISummaryCards';

/**
 * Entry type badge with direction indicator.
 */
const EntryTypeBadge = ({ entryType }) => {
  const isInflow = ['CREDIT_IN', 'SETTLEMENT_IN'].includes((entryType || '').toUpperCase());
  return (
    <div className="flex items-center gap-1">
      {isInflow ? (
        <ArrowUpward sx={{ fontSize: 14, color: '#10b981' }} />
      ) : (
        <ArrowDownward sx={{ fontSize: 14, color: '#ef4444' }} />
      )}
      <Chip
        label={(entryType || 'UNKNOWN').replace(/_/g, ' ')}
        size="small"
        variant="outlined"
        color={isInflow ? 'success' : 'error'}
        sx={{ fontWeight: 600, fontSize: '0.6rem', height: 22 }}
      />
    </div>
  );
};

/**
 * EscrowTab
 *
 * Shows all escrow ledger entries across all credit cases.
 */
const EscrowTab = ({ entries = [], balances = {}, loading = false }) => {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');

  const types = ['ALL', 'CREDIT_IN', 'VENDOR_PAYMENT', 'SETTLEMENT_IN', 'FARMER_PAYOUT', 'LOAN_PRINCIPAL', 'LOAN_INTEREST', 'INSURANCE_PREMIUM'];

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      const matchSearch =
        !search ||
        (e.reference_id || '').toLowerCase().includes(search.toLowerCase()) ||
        (e.credit_case_id?.toString() || '').includes(search);
      const matchType =
        typeFilter === 'ALL' ||
        (e.entry_type || '').toUpperCase() === typeFilter;
      return matchSearch && matchType;
    });
  }, [entries, search, typeFilter]);

  // Summary stats
  const totalInflow = entries
    .filter((e) => ['CREDIT_IN', 'SETTLEMENT_IN'].includes((e.entry_type || '').toUpperCase()))
    .reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const totalOutflow = entries
    .filter((e) => !['CREDIT_IN', 'SETTLEMENT_IN'].includes((e.entry_type || '').toUpperCase()))
    .reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-10 bg-gray-200 rounded w-full" />
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-12 bg-gray-100 rounded w-full" />
        ))}
      </div>
    );
  }

  return (
    <div>
      {/* Summary row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4">
          <p className="text-xs text-emerald-600 font-medium">Total Inflow</p>
          <p className="text-lg font-bold text-emerald-800">{formatCurrency(totalInflow)}</p>
        </div>
        <div className="rounded-xl bg-red-50 border border-red-100 p-4">
          <p className="text-xs text-red-600 font-medium">Total Outflow</p>
          <p className="text-lg font-bold text-red-800">{formatCurrency(totalOutflow)}</p>
        </div>
        <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-4">
          <p className="text-xs text-indigo-600 font-medium">Net Balance</p>
          <p className="text-lg font-bold text-indigo-800">{formatCurrency(totalInflow - totalOutflow)}</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
        <TextField
          placeholder="Search by case ID or reference..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: '#9ca3af' }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 280 }}
        />

        <div className="flex items-center gap-1 flex-wrap">
          <FilterList sx={{ fontSize: 16, color: '#9ca3af', mr: 0.5 }} />
          {types.slice(0, 5).map((t) => (
            <Chip
              key={t}
              label={t === 'ALL' ? 'All' : t.replace(/_/g, ' ')}
              variant={typeFilter === t ? 'filled' : 'outlined'}
              color={typeFilter === t ? 'primary' : 'default'}
              size="small"
              onClick={() => setTypeFilter(t)}
              sx={{ fontWeight: 500, fontSize: '0.6rem', cursor: 'pointer' }}
            />
          ))}
        </div>

        <div className="ml-auto">
          <Tooltip title="Export Ledger CSV">
            <IconButton size="small">
              <Download sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      <p className="text-xs text-gray-400 mb-3">
        Showing {filtered.length} of {entries.length} entries
      </p>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left">
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Case ID</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Type</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Debit</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Credit</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Amount</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Reference</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-gray-400 text-sm">
                  No escrow ledger entries found
                </td>
              </tr>
            ) : (
              filtered.map((entry, idx) => (
                <tr key={entry.id || idx} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">#{entry.credit_case_id || entry.account_id}</td>
                  <td className="px-4 py-3">
                    <EntryTypeBadge entryType={entry.entry_type} />
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">{entry.debit_account || '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{entry.credit_account || '—'}</td>
                  <td className="px-4 py-3 font-semibold text-gray-800">
                    {formatCurrency(parseFloat(entry.amount) || 0)}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 font-mono">{entry.reference_id || '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {entry.created_at
                      ? new Date(entry.created_at).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })
                      : '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EscrowTab;
